<template>
  <modal name="modal-image-editor" transition="nice-modal-fade" classes="ngdialog-content" :pivot-y="0.5" :clickToClose="false"
    :adaptive="true" :scrollable="true" :zoomable='true' :reset="true" width="600" height="auto" @before-open="beforeOpen"
    @opened="onOpened" @before-close="beforeClose">
    <div class="modal_wrapper">
      <div class="d-flex justify-content-between align-items-center mb-3" slot="top-right">
        <h6 style="color:#C43BA7; margin: 0;">{{ $t('pickers.editori') || 'Image Editor' }}</h6>
        <button @click="$modal.hide('modal-image-editor')" class="ngdialog-close"></button>
      </div>

      <div class="editor-container" v-if="imgSrc">
        <vue-cropper
          ref="cropper"
          :src="imgSrc"
          :check-cross-origin="true"
          alt="Source Image"
          :view-mode="1"
          :drag-mode="dragMode"
          :aspect-ratio="aspect"
          :auto-crop="true"
          :auto-crop-area="0.8"
          :background="true"
          :rotatable="true"
          :zoomable="true"
          :guides="true"
          :highlight="true"
          :crop-box-movable="true"
          :crop-box-resizable="true"
          :toggle-drag-mode-on-dblclick="true"
          style="width: 100%; height: 400px;"
          @ready="onReady"
        >
        </vue-cropper>
      </div>
      <div v-else class="d-flex justify-content-center align-items-center" style="height: 400px; background: #f0f0f0;">
        <p>Loading...</p>
      </div>

      <div class="controls mt-3" v-if="imgSrc">
        <div class="d-flex justify-content-center flex-wrap gap-2">
            <vs-button-group>
                <vs-button flat icon @click="zoom(0.1)" title="Zoom In">
                    <span><Icon icon="ph:magnifying-glass-plus-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="zoom(-0.1)" title="Zoom Out">
                    <span><Icon icon="ph:magnifying-glass-minus-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="rotate(-90)" title="Rotate Left">
                    <span><Icon icon="ph:arrow-counter-clockwise-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="rotate(90)" title="Rotate Right">
                    <span><Icon icon="ph:arrow-clockwise-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="scaleX()" title="Flip Horizontal">
                     <span><Icon icon="ph:arrows-horizontal-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="scaleY()" title="Flip Vertical">
                    <span><Icon icon="ph:arrows-vertical-light" style="font-size: 20px;" /></span>
                </vs-button>
                 <vs-button flat icon @click="setDragMode('move')" :active="dragMode === 'move'" title="Move Image">
                    <span><Icon icon="ph:hand-grabbing-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="setDragMode('crop')" :active="dragMode === 'crop'" title="Crop">
                    <span><Icon icon="ph:crop-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="reset()" title="Reset">
                    <span><Icon icon="ph:arrow-u-up-left-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="forceCrop()" title="Force Crop Box">
                    <span><Icon icon="ph:corners-out-light" style="font-size: 20px;" /></span>
                </vs-button>
                <vs-button flat icon @click="toggleAspect()" :title="isNaN(aspect) ? 'Lock Aspect Ratio' : 'Unlock Aspect Ratio'">
                    <span><Icon :icon="isNaN(aspect) ? 'ph:lock-key-open-light' : 'ph:lock-key-light'" style="font-size: 20px;" /></span>
                </vs-button>
            </vs-button-group>
        </div>
        
        <div class="d-flex justify-content-center mt-3">
             <vs-button @click="save" :loading="saving">
              <Icon icon="ph:floppy-disk-light" style="font-size: 20px; margin-right: 5px;" />
              {{ $t('general.guardar') || 'Save' }}
            </vs-button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { Icon } from "@iconify/vue2";

export default {
  components: {
    Icon
  },
  data() {
    return {
      imgSrc: null,
      aspect: NaN, // NaN means free aspect ratio
      initialAspect: NaN,
      parent: null,
      blobImage: null,
      dragMode: 'crop',
      scaleXVal: 1,
      scaleYVal: 1,
      saving: false,
      loading: true
    };
  },
  mounted() {
      console.log('modal-image-editor mounted');
  },
  watch: {
      aspect(newVal) {
          if (this.$refs.cropper) {
              this.$refs.cropper.setAspectRatio(newVal);
          }
      }
  },
  methods: {
    beforeOpen(event) {
      this.parent = event.params.parent;
      this.blobImage = event.params.blobImage || (this.parent && this.parent.blobImage) || null;
      this.loadParentImage();
      
      if (this.parent.aspect) {
          this.initialAspect = parseFloat(this.parent.aspect);
      } else if (this.parent.isCircle) {
           this.initialAspect = 1;
      } else {
          this.initialAspect = NaN;
      }
      // Force Free Aspect Ratio by default as requested
      this.aspect = NaN;
    },
    toggleAspect() {
        if (isNaN(this.aspect)) {
             this.aspect = isNaN(this.initialAspect) ? 1 : this.initialAspect;
        } else {
            this.aspect = NaN;
        }
    },
    async loadParentImage() {
        if (!this.parent) return;

        const blobSource = this.blobImage || this.parent.blobImage;
        if (blobSource instanceof Blob || blobSource instanceof File) {
             this.setImgSrcFromBlob(blobSource);
             return;
        }

        let imageSource = this.parent.image;

        if (typeof imageSource === 'string') {
            // Fetch the image as a blob to handle CORS properly and create a local URL
            if (imageSource.includes('firebasestorage') || imageSource.startsWith('http')) {
                try {
                    const separator = imageSource.includes('?') ? '&' : '?';
                    const proxiedUrl = `${imageSource}${separator}cors_t=${new Date().getTime()}`;
                    const response = await fetch(proxiedUrl, { mode: 'cors' });
                    if (!response.ok) throw new Error('Network response was not ok');
                    const blob = await response.blob();
                    this.setImgSrcFromBlob(blob);
                } catch (e) {
                    console.error("Failed to fetch image as blob, falling back to direct URL", e);
                    this.imgSrc = imageSource;
                }
            } else {
                this.imgSrc = imageSource;
            }
        } else if (typeof imageSource === 'object' && imageSource && imageSource.obj) {
             this.imgSrc = imageSource.obj;
        } else if (typeof imageSource === 'object' && imageSource && imageSource.default) {
             // Handle require/import images
             this.fetchImageArgs(imageSource.default);
        } else {
            console.error('Unknown image source format', imageSource);
        }
    },
    setImgSrcFromBlob(blob) {
        // Clean up previous object URL if it exists
        if (this.imgSrc && this.imgSrc.startsWith('blob:')) {
            URL.revokeObjectURL(this.imgSrc);
        }
        this.imgSrc = URL.createObjectURL(blob);
    },
    readImageFile(file) {
        if (!file.type.match('image.*')) {
            alert('Please select an image file');
            return;
        }
        this.setImgSrcFromBlob(file);
    },
    async fetchImageArgs(url) {
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            this.setImgSrcFromBlob(blob);
        } catch (e) {
            console.error("Failed to fetch image from url, falling back to direct URL usage", url, e);
            this.imgSrc = url;
        }
    },
    onOpened() {
        this.$nextTick(() => {
            this.resizeCropper();
        });
    },
    resizeCropper() {
        if (!this.$refs.cropper) return;

        try {
            if (this.$refs.cropper.cropper && this.$refs.cropper.$el && this.$refs.cropper.$el.offsetWidth > 0) {
                this.$refs.cropper.cropper.resize();
            }
        } catch (e) {
            console.warn("Could not resize cropper on open", e);
        }
    },
    beforeClose() {
      // Clean up object URL to prevent memory leaks
      if (this.imgSrc && this.imgSrc.startsWith('blob:')) {
          URL.revokeObjectURL(this.imgSrc);
      }
      this.imgSrc = null;
      this.blobImage = null;
      this.scaleXVal = 1;
      this.scaleYVal = 1;
      this.saving = false;
      this.aspect = NaN;
    },
    onReady() {
        console.log("Cropper ready");
        this.loading = false;
        
        if (this.$refs.cropper) {
            // Check if crop method exists before calling
            if (typeof this.$refs.cropper.crop === 'function') {
                this.$refs.cropper.crop();
            } else if (typeof this.$refs.cropper.enable === 'function') {
                 // Try enable if crop is not available (like in modalCrop)
                 this.$refs.cropper.enable();
            }

            this.$refs.cropper.setDragMode('crop');

            // Manual crop box positioning (from modalCrop.vue)
            try {
                 const containerData = this.$refs.cropper.getContainerData();
                 const size = Math.min(containerData.width, containerData.height) * 0.8;
                 
                 // Check if setCropBoxData exists
                 if (typeof this.$refs.cropper.setCropBoxData === 'function') {
                    this.$refs.cropper.setCropBoxData({
                        left: (containerData.width - size) / 2,
                        top: (containerData.height - size) / 2,
                        width: size,
                        height: size
                    });
                 }
            } catch (e) {
                console.error("Error setting custom crop box", e);
            }

            this.resizeCropper();
        }
    },
    
    // Editor controls
    zoom(percent) {
        if(this.$refs.cropper) this.$refs.cropper.relativeZoom(percent);
    },
    rotate(deg) {
        if(this.$refs.cropper) this.$refs.cropper.rotate(deg);
    },
    scaleX() {
        if(!this.$refs.cropper) return;
        this.scaleXVal = this.scaleXVal * -1;
        this.$refs.cropper.scaleX(this.scaleXVal);
    },
    scaleY() {
        if(!this.$refs.cropper) return;
        this.scaleYVal = this.scaleYVal * -1;
        this.$refs.cropper.scaleY(this.scaleYVal);
    },
    setDragMode(mode) {
        if(!this.$refs.cropper) return;
        this.dragMode = mode;
        this.$refs.cropper.setDragMode(mode);
    },
    reset() {
        if(this.$refs.cropper) {
            this.$refs.cropper.reset();
            this.scaleXVal = 1;
            this.scaleYVal = 1;
        }
    },
    forceCrop() {
        if(this.$refs.cropper) {
             if (typeof this.$refs.cropper.crop === 'function') {
                this.$refs.cropper.crop();
            }
            this.$refs.cropper.setDragMode('crop');
        }
    },

    save() {
        if (!this.$refs.cropper) return;
        this.saving = true;
        console.log("Saving cropped image...");

        try {
            // Get the cropped canvas at its natural size first for dimension checks
            const naturalCanvas = this.$refs.cropper.getCroppedCanvas();

            if (!naturalCanvas) {
                throw new Error("Could not generate canvas from cropper. The image might be too large or invalid.");
            }

            if (this.parent && this.parent.imgid === 'seo') {
                const width = naturalCanvas.width;
                const height = naturalCanvas.height;
                if (width < 300 || height < 300) {
                    const msg = `Imagen demasiado pequeña (${width}x${height}px). El mínimo para SEO es 300x300px para asegurar que se vea en redes sociales.`;
                    if (this.$util && this.$util.notif_error) this.$util.notif_error(msg);
                    else alert(msg);
                    this.saving = false;
                    return;
                }
            }

            // Limit output size to avoid memory issues and silent failures on large images
            const canvas = this.$refs.cropper.getCroppedCanvas({
                maxWidth: 4096,
                maxHeight: 4096,
                fillColor: '#fff',
                imageSmoothingQuality: 'high',
            });
            
            if (!canvas) {
                throw new Error("Could not generate canvas from cropper. The image might be too large or invalid.");
            }

            console.log(`Canvas generated: ${canvas.width}x${canvas.height}`);

            let processCanvas = canvas;
            
            // Handle Circle Crop simulation for output if needed
            if (this.parent.isCircle) {
                processCanvas = this.getRoundedCanvas(canvas);
            }

            let mimeType = 'image/png';
            let quality = 0.9;
            let extension = 'png';

            if (this.parent && this.parent.imgid === 'seo') {
                mimeType = 'image/jpeg';
                quality = 0.8;
                extension = 'jpg';
            }

            processCanvas.toBlob((blob) => {
                if (!blob) {
                    this.saving = false;
                    const errorMsg = "Fallo al crear el archivo de imagen (Blob).";
                    console.error(errorMsg);
                    if (this.$util && this.$util.notif_error) this.$util.notif_error(errorMsg);
                    return;
                }

                console.log(`Blob created: ${blob.size} bytes`);

                // Create a new File object from the blob to mimic file upload
                const fileName = `edited_image_${new Date().getTime()}.${extension}`;
                const file = new File([blob], fileName, { type: mimeType });

                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result;
                    
                     // Construct the object expected by imagePicker
                    const obj = {
                       obj: base64data,
                       file: file,
                       imgid: this.parent ? this.parent.imgid : 'unknown', // pass through identity
                       ext: extension,
                       file_type: file // legacy compatibility
                    };

                    console.log("Emitting data to parent...");
                    // Emit back to parent
                    this.parent.$emit('input', obj);
                    
                    // If using global bus
                    if (this.$util && this.$util.pushToBus) {
                        this.$util.pushToBus(obj);
                    }

                    this.$modal.hide('modal-image-editor');
                    this.saving = false;
                };
                reader.onerror = (e) => {
                    console.error("FileReader error", e);
                    this.saving = false;
                };
                reader.readAsDataURL(blob);
            }, mimeType, quality);
        } catch (e) {
            console.error("Error saving image", e);
            let message = "No se puede guardar esta imagen.";
            if (e.message && e.message.includes('tainted')) {
                message = "No se puede guardar debido a restricciones de seguridad (CORS). Intente descargar y subir la imagen de nuevo.";
            } else if (e.message) {
                message += " Error: " + e.message;
            }
            
            if (this.$util && this.$util.notif_error) {
                this.$util.notif_error(message);
            } else {
                alert(message);
            }
            this.saving = false;
        }
    },
    getRoundedCanvas(sourceCanvas) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const width = sourceCanvas.width;
      const height = sourceCanvas.height;

      canvas.width = width;
      canvas.height = height;
      context.imageSmoothingEnabled = true;
      context.drawImage(sourceCanvas, 0, 0, width, height);
      context.globalCompositeOperation = 'destination-in';
      context.beginPath();
      context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI, true);
      context.fill();
      return canvas;
    }
  }
};
</script>

<style scoped>
.modal_wrapper {
  padding: 15px;
  background: white; 
  /* Ensure background is white if not inherited */
}
.editor-container {
    background: #333; /* Dark background for editor to contrast image */
}
.gap-2 {
    gap: 0.5rem;
}
</style>
